import {MutationCache, QueryClient} from '@tanstack/react-query';

import './mutationMeta';

/**
 * Le client React Query de l'app, avec ses défauts et le filet global des refus.
 *
 * FILET GLOBAL : toute mutation rejetée appelle `onMutationFailure`, qui l'affiche en
 * snackbar (cf. `App.tsx`). Avant lui, un refus ne s'affichait que si l'écran appelant y
 * pensait : 108 hooks de mutation, une poignée d'appelants qui montraient l'erreur, et pour
 * les autres un clic sans effet (PERFECTPOST-WEB-10A / 109). Un écran qui affiche lui-même
 * le refus s'exclut avec `meta: {handlesApiFailure: true}` (`HANDLES_API_FAILURE`).
 *
 * Les REQUÊTES n'ont pas de filet : une lecture qui échoue se rejoue (refetch au focus, à
 * l'invalidation, `retry`), et chacune de ces reprises ferait un snackbar ; plusieurs
 * requêtes tombent d'ailleurs ensemble à la moindre coupure réseau. Une lecture refusée
 * laisse un état vide ou un chargement, que l'écran sait déjà afficher — aucune action de
 * l'utilisateur ne reste sans réponse, à la différence d'un bouton dont la mutation échoue.
 */
export function createQueryClient(onMutationFailure: (error: unknown) => void): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) => {
        if (mutation.meta?.handlesApiFailure === true) {
          return;
        }
        onMutationFailure(error);
      },
    }),
    // Défauts explicites du cache serveur.
    //
    // Ils remplacent le `new QueryClient()` nu, dont les valeurs par défaut de
    // TanStack v5 (`staleTime: 0`, `retry: 3`) sont mal adaptées ici : `staleTime: 0`
    // marque chaque réponse comme périmée dès son arrivée, donc deux composants qui
    // montent à la suite refetchent tous les deux — c'est exactement le
    // dédoublonnage qu'on vient chercher en remplaçant Redux.
    //
    // Ce bloc est le pendant direct de ce que faisait le store : les données
    // restaient en mémoire jusqu'à ce qu'un thunk les remplace. `staleTime` est la
    // version bornée dans le temps de ce comportement.
    defaultOptions: {
      queries: {
        // Une donnée reste fraîche 1 min : le temps de naviguer entre deux pages
        // qui affichent la même ressource sans relancer de requête.
        staleTime: 60_000,
        // Conservée 5 min après le démontage du dernier consommateur — un
        // aller-retour vers une autre vue et retour n'entraîne pas de rechargement.
        gcTime: 5 * 60_000,
        // Une seule reprise. Les erreurs qui comptent ici sont des 4xx (session
        // expirée, droits) que réessayer trois fois ne corrige pas : ça ne fait
        // que retarder l'affichage de l'erreur de plusieurs secondes.
        retry: 1,
        // Le refetch au retour d'onglet est le défaut de TanStack. On le garde,
        // mais `staleTime` ci-dessus le rend beaucoup moins bavard.
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Une mutation n'est jamais rejouée automatiquement : republier un post ou
        // recréer un brouillon sur un timeout réseau produirait un doublon côté
        // API. C'est à l'appelant de proposer un nouvel essai.
        retry: 0,
      },
    },
  });
}
